// Éditeur d'un formulaire d'accréditation (= un type d'accréditation / tarif Wilout) : libellé,
// jours de présence proposés (ou durée complète), règles cashless (crédits + unités A-D).
// Pas de prix ni de préfixe de tarif ici : une accréditation est toujours gratuite (prix forcé
// à 0 côté backend), et le nom de tarif est construit à partir du seul libellé de zone
// (cf. accreditationRowBuilder.buildTarifName) — aucun des deux n'a besoin d'être saisi.
// Composant indépendant de components/tools/AccreditationConverterTool.js — même esprit que son
// `renderRuleRow`, pas de code partagé, pour ne jamais risquer l'outil existant.
import React, { useState } from 'react';
import {
  Box, Paper, Stack, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, Button, Divider, Checkbox, IconButton,
  ToggleButtonGroup, ToggleButton, Alert
} from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';
import CashlessRuleRow, { withDefaultRules } from './CashlessRuleRow';

// key omise volontairement : un champ neuf n'en a pas encore, le serveur en assigne une à la
// création (cf. accreditationFormService.sanitizeCustomFields) — ne jamais en inventer une ici.
// `options` est édité comme une CHAÎNE "S, M, L" (pas un tableau) tant qu'on est dans l'éditeur :
// dériver l'affichage d'un tableau reparsé à chaque frappe empêcherait de taper une virgule
// (elle disparaîtrait aussitôt du champ contrôlé, rendant impossible la saisie d'une 2e option).
// Reconverti en tableau juste avant l'enregistrement, cf. handleSave.
const emptyCustomField = () => ({ label: '', type: 'text', required: false, options: '' });

const emptyForm = () => ({
  mode: 'standard',
  label: '',
  zoneLabel: '',
  welcomeTitle: '',
  welcomeText: '',
  fullDuration: false,
  eventDays: [],
  barcodeLength: 15,
  tarifPrefix: '',
  customFields: [],
  poleCashless: false,
  rules: withDefaultRules(),
});

const AccreditationFormEditor = ({ initialForm, onSave, onCancel, saving, projectEventDays = [] }) => {
  const [form, setForm] = useState(() => ({
    ...emptyForm(),
    ...initialForm,
    welcomeTitle: initialForm?.welcomeTitle || '',
    welcomeText: initialForm?.welcomeText || '',
    // Les champs personnalisés existants arrivent du serveur avec `options: string[]` — convertis
    // en chaîne éditable ici (cf. commentaire sur emptyCustomField).
    customFields: (initialForm?.customFields || []).map((f) => ({
      ...f,
      options: Array.isArray(f.options) ? f.options.join(', ') : (f.options || ''),
    })),
    rules: withDefaultRules(initialForm?.rules),
  }));

  const patch = (fields) => setForm((f) => ({ ...f, ...fields }));

  const patchRule = (key, fields) => {
    if (key === 'credit') {
      setForm((f) => ({ ...f, rules: { ...f.rules, credit: { ...f.rules.credit, ...fields } } }));
    } else {
      setForm((f) => ({
        ...f,
        rules: { ...f.rules, units: { ...f.rules.units, [key]: { ...f.rules.units[key], ...fields } } },
      }));
    }
  };

  // Les jours proposés à ce formulaire sont un SOUS-ENSEMBLE des jours du projet (définis une
  // seule fois, cf. AccreditationConfigApp) — plus de saisie libre de date/libellé ici.
  const toggleProjectDay = (day) => {
    const has = form.eventDays.some((d) => d.label === day.label);
    patch({
      eventDays: has
        ? form.eventDays.filter((d) => d.label !== day.label)
        : [...form.eventDays, day],
    });
  };

  const dayLabels = form.eventDays.map((d) => d.label).filter(Boolean);

  // Champs personnalisés (un jeu par personne dans une soumission — cf. AccreditationPublicFormPage).
  // `options` est édité ici comme une chaîne "S, M, L" pour rester simple ; reconverti en tableau
  // trié/dédupliqué juste avant l'enregistrement (cf. onSave ci-dessous).
  const addCustomField = () => patch({ customFields: [...form.customFields, emptyCustomField()] });
  const updateCustomField = (idx, fields) =>
    patch({ customFields: form.customFields.map((f, i) => (i === idx ? { ...f, ...fields } : f)) });
  const removeCustomField = (idx) => patch({ customFields: form.customFields.filter((_, i) => i !== idx) });

  const canSubmit = form.label.trim().length > 0 && !saving;

  // Reconvertit les options "S, M, L" (chaîne éditée) en tableau juste avant l'enregistrement —
  // cf. commentaire sur emptyCustomField pour pourquoi ce n'est pas fait à chaque frappe.
  const handleSave = () => {
    onSave({
      ...form,
      customFields: form.customFields.map((f) => ({
        ...f,
        options: typeof f.options === 'string'
          ? f.options.split(',').map((o) => o.trim()).filter(Boolean)
          : f.options,
      })),
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
        {initialForm?.id ? 'Modifier le formulaire' : 'Nouveau formulaire'}
      </Typography>

      <Stack spacing={3}>
        {!initialForm?.id ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Type de formulaire</Typography>
            <ToggleButtonGroup
              size="small" exclusive value={form.mode}
              onChange={(e, next) => { if (next) patch({ mode: next }); }}
            >
              <ToggleButton value="standard">Standard (un type d'accès)</ToggleButton>
              <ToggleButton value="pole">Chef de pôle</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {form.mode === 'pole'
                ? "Le chef de pôle saisit son équipe et choisit un type d'accès pour chaque personne, parmi ceux de l'événement (section « Types d'accès » en haut de page). Le libellé ci-dessous sert à nommer son lien."
                : "Le libellé du formulaire est le type d'accès : il devient le nom du tarif."}
            </Typography>
          </Box>
        ) : (
          form.mode === 'pole' && <Alert severity="info">Formulaire « chef de pôle » : le type d'accès est choisi pour chaque personne saisie.</Alert>
        )}

        <TextField
          label="Libellé (ex. Bénévole, Presse, VIP)"
          value={form.label}
          onChange={(e) => patch({ label: e.target.value })}
          placeholder="Bénévole"
          fullWidth
        />

        <TextField
          label="Titre affiché sur le formulaire public (optionnel)"
          value={form.welcomeTitle}
          onChange={(e) => patch({ welcomeTitle: e.target.value })}
          placeholder={form.label || 'Reprend le libellé si laissé vide'}
          helperText="Remplace le libellé comme titre vu par les personnes qui remplissent le formulaire."
          fullWidth
        />

        <TextField
          label="Texte d'accueil affiché sur le formulaire public (optionnel)"
          value={form.welcomeText}
          onChange={(e) => patch({ welcomeText: e.target.value })}
          placeholder="Ex. Merci de votre présence, remplissez ce formulaire pour obtenir votre accréditation…"
          helperText="Affiché sous le titre, avant les champs à remplir."
          multiline
          minRows={3}
          fullWidth
        />

        <FormControlLabel
          control={<Switch checked={form.fullDuration} onChange={(e) => patch({ fullDuration: e.target.checked })} />}
          label="Accréditation valable sur toute la durée de l'événement (pas de choix de jour)"
        />

        {!form.fullDuration && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Jours proposés au sélecteur (parmi les jours de l'événement)
            </Typography>
            {projectEventDays.length === 0 ? (
              <Alert severity="info">
                Définissez d'abord les jours de l'événement ci-dessus (section "Jours de
                l'événement"), avant de pouvoir en choisir pour ce formulaire.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {projectEventDays.map((d) => {
                  const checked = form.eventDays.some((fd) => fd.label === d.label);
                  return (
                    <FormControlLabel
                      key={d.label}
                      control={<Checkbox checked={checked} onChange={() => toggleProjectDay(d)} />}
                      label={d.date ? `${d.label} (${d.date})` : d.label}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        <Box>
          <Divider sx={{ mb: 2 }} />
          {form.mode === 'pole' && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Switch checked={form.poleCashless} onChange={(e) => patch({ poleCashless: e.target.checked })} />}
                label="Le chef de pôle peut régler le cashless (règles par type d'accès et valeurs par personne)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Activé, le chef de pôle voit ses propres règles et peut saisir crédits et unités pour chaque personne.
                Les règles ci-dessous s'appliquent aux personnes pour lesquelles il ne règle rien. Les valeurs apparaissent
                dans la liste des demandes avant l'export.
              </Typography>
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Règles cashless (crédits et unités A-D)
          </Typography>
          <CashlessRuleRow label="Crédits" rule={form.rules.credit} dayLabels={dayLabels} onChange={(f) => patchRule('credit', f)} />
          {['A', 'B', 'C', 'D'].map((u) => (
            <CashlessRuleRow
              key={u} label={`Unité ${u}`} rule={form.rules.units[u]} dayLabels={dayLabels}
              onChange={(f) => patchRule(u, f)}
            />
          ))}
        </Box>

        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Champs personnalisés (ex. Taille t-shirt, Société…)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Chaque personne de la soumission remplit ses propres réponses. Exportables en CSV.
          </Typography>
          <Stack spacing={1.5}>
            {form.customFields.map((field, idx) => (
              <Stack key={field.key || `new-${idx}`} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <TextField
                  size="small" label="Libellé" value={field.label}
                  onChange={(e) => updateCustomField(idx, { label: e.target.value })}
                  placeholder="Ex. Taille t-shirt" sx={{ flex: 1, minWidth: 160 }}
                />
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type" value={field.type}
                    onChange={(e) => updateCustomField(idx, { type: e.target.value })}
                  >
                    <MenuItem value="text">Texte libre</MenuItem>
                    <MenuItem value="select">Liste déroulante</MenuItem>
                  </Select>
                </FormControl>
                {field.type === 'select' && (
                  <TextField
                    size="small" label="Options (séparées par des virgules)" value={field.options}
                    onChange={(e) => updateCustomField(idx, { options: e.target.value })}
                    placeholder="S, M, L, XL" sx={{ flex: 1, minWidth: 200 }}
                  />
                )}
                <FormControlLabel
                  control={<Checkbox checked={field.required} onChange={(e) => updateCustomField(idx, { required: e.target.checked })} />}
                  label="Obligatoire"
                />
                <IconButton size="small" color="error" onClick={() => removeCustomField(idx)}>
                  <Trash2 size={16} />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Button startIcon={<Plus size={16} />} onClick={addCustomField} size="small" sx={{ mt: 1.5 }}>
            Ajouter un champ
          </Button>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" disabled={!canSubmit} onClick={handleSave}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          <Button variant="outlined" color="inherit" onClick={onCancel} disabled={saving}>
            Annuler
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default AccreditationFormEditor;
